import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Globe,
  Mail,
  RefreshCcw,
  Settings,
  Shield,
  Sparkles,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const StatusBadge = ({ ok, label, testId }) => (
  <Badge className={ok ? "bg-green-600" : "bg-slate-500"} data-testid={testId}>
    {label}
  </Badge>
);

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "—");

export const AdminSettingsPanel = ({ API, mfaStatus, refreshMfaStatus }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domainData, setDomainData] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [securityData, setSecurityData] = useState(null);
  const [mailboxPasswords, setMailboxPasswords] = useState({});
  const [setupCode, setSetupCode] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [setupPayload, setSetupPayload] = useState(null);

  const [aiForm, setAiForm] = useState({
    enabled: true,
    confidence_threshold: 0.78,
    manual_review_threshold: 0.55,
    model_name: "gemini-flash-latest",
  });

  const [emailForm, setEmailForm] = useState({
    provider_name: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_username: "",
    imap_host: "",
    imap_port: 993,
    imap_use_ssl: true,
    dkim_selector: "",
    support_email: "sanjib@friendshipad.com",
    notification_emails: "sanjib@friendshipad.com,siddhran@friendshipad.com,snigdha@friendshipad.com",
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [domainRes, aiRes, emailRes, securityRes] = await Promise.all([
        axios.get(`${API}/admin/settings/domain-status`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/ai-verification`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/email`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/security`, { withCredentials: true }),
      ]);

      setDomainData(domainRes.data);
      setAiData(aiRes.data);
      setEmailData(emailRes.data);
      setSecurityData(securityRes.data);
      setAiForm({
        enabled: aiRes.data.enabled,
        confidence_threshold: aiRes.data.confidence_threshold,
        manual_review_threshold: aiRes.data.manual_review_threshold,
        model_name: aiRes.data.model_name,
      });
      setEmailForm({
        provider_name: emailRes.data.provider_name || "",
        smtp_host: emailRes.data.smtp_host || "",
        smtp_port: emailRes.data.smtp_port || 587,
        smtp_use_tls: !!emailRes.data.smtp_use_tls,
        smtp_use_ssl: !!emailRes.data.smtp_use_ssl,
        smtp_username: emailRes.data.smtp_username || "",
        imap_host: emailRes.data.imap_host || "",
        imap_port: emailRes.data.imap_port || 993,
        imap_use_ssl: !!emailRes.data.imap_use_ssl,
        dkim_selector: emailRes.data.dkim_selector || "",
        support_email: emailRes.data.support_email || "sanjib@friendshipad.com",
        notification_emails: (emailRes.data.notification_emails || []).join(","),
      });
      setSetupPayload(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const suspiciousCount = useMemo(() => aiData?.suspicious_activity?.length || 0, [aiData]);

  const saveAiSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/ai-verification`, aiForm, { withCredentials: true });
      toast.success("AI verification settings updated.");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save AI settings.");
    } finally {
      setSaving(false);
    }
  };

  const testGemini = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/settings/gemini/test`, {}, { withCredentials: true });
      toast.success(response.data.message || "Gemini connection successful.");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gemini test failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings/email`,
        {
          ...emailForm,
          smtp_port: Number(emailForm.smtp_port),
          imap_port: Number(emailForm.imap_port),
          notification_emails: emailForm.notification_emails
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
        },
        { withCredentials: true }
      );
      toast.success("Email settings updated.");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save email settings.");
    } finally {
      setSaving(false);
    }
  };

  const updateMailboxPassword = async (email) => {
    const password = mailboxPasswords[email];
    if (!password) {
      toast.error("Enter a password first.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/settings/email/account-password`,
        { account_email: email, password },
        { withCredentials: true }
      );
      toast.success(`Password updated for ${email}.`);
      setMailboxPasswords((prev) => ({ ...prev, [email]: "" }));
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update mailbox password.");
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/settings/email/test`, {}, { withCredentials: true });
      toast.success(response.data.message || "Email test succeeded.");
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email test failed.");
    } finally {
      setSaving(false);
    }
  };

  const startMfaSetup = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/mfa/setup`, {}, { withCredentials: true });
      setSetupPayload(response.data);
      toast.success("Scan the QR code and verify the setup.");
      await refreshMfaStatus?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start MFA setup.");
    } finally {
      setSaving(false);
    }
  };

  const confirmMfaSetup = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mfa/verify-setup`, { code: setupCode }, { withCredentials: true });
      toast.success("Admin MFA enabled.");
      setSetupCode("");
      setSetupPayload(null);
      await refreshMfaStatus?.();
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify MFA setup.");
    } finally {
      setSaving(false);
    }
  };

  const verifyMfaSession = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mfa/verify`, { code: verifyCode }, { withCredentials: true });
      toast.success("Admin MFA verified for this session.");
      setVerifyCode("");
      await refreshMfaStatus?.();
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify MFA code.");
    } finally {
      setSaving(false);
    }
  };

  const disableMfa = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mfa/disable`, { code: disableCode }, { withCredentials: true });
      toast.success("Admin MFA disabled.");
      setDisableCode("");
      await refreshMfaStatus?.();
      await loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable MFA.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-sm text-[#475569]">Loading settings...</div>;
  }

  return (
    <Tabs defaultValue="website" className="space-y-6">
      <TabsList className="grid w-full grid-cols-2 gap-2 rounded-2xl bg-white p-2 md:grid-cols-6">
        <TabsTrigger value="website" data-testid="settings-website-tab">Website</TabsTrigger>
        <TabsTrigger value="domain" data-testid="settings-domain-tab">Domain</TabsTrigger>
        <TabsTrigger value="gemini" data-testid="settings-gemini-tab">Gemini AI</TabsTrigger>
        <TabsTrigger value="verification" data-testid="settings-ai-verification-tab">AI Verification</TabsTrigger>
        <TabsTrigger value="email" data-testid="settings-email-tab">Email</TabsTrigger>
        <TabsTrigger value="security" data-testid="settings-security-tab">Security</TabsTrigger>
      </TabsList>

      <TabsContent value="website" className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {[
            {
              title: "Primary website",
              description: domainData?.primary_domain,
              ok: !!domainData?.primary_http?.reachable,
              meta: domainData?.primary_http?.reachable ? `HTTP ${domainData.primary_http.status_code}` : domainData?.primary_http?.error,
              icon: Globe,
              testId: "website-primary-status",
            },
            {
              title: "WWW website",
              description: domainData?.www_domain,
              ok: !!domainData?.www_http?.reachable,
              meta: domainData?.www_http?.reachable ? `HTTP ${domainData.www_http.status_code}` : domainData?.www_http?.error,
              icon: Globe,
              testId: "website-www-status",
            },
            {
              title: "SSL status",
              description: domainData?.ssl_status?.valid ? "Certificate valid" : "Certificate issue",
              ok: !!domainData?.ssl_status?.valid,
              meta: domainData?.ssl_status?.valid
                ? `Expires ${formatDateTime(domainData.ssl_status.expires_at)}`
                : domainData?.ssl_status?.error,
              icon: Shield,
              testId: "website-ssl-status",
            },
            {
              title: "DNS status",
              description: domainData?.nameservers?.length ? "DNS records detected" : "DNS pending",
              ok: !!domainData?.a_records?.length,
              meta: domainData?.a_records?.length ? `A record: ${domainData.a_records.join(", ")}` : "A record missing",
              icon: Settings,
              testId: "website-dns-status",
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.title} className="neumorphic-card rounded-3xl">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0EA5E9]/10 text-[#0EA5E9]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <StatusBadge ok={item.ok} label={item.ok ? "Healthy" : "Attention"} testId={item.testId} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A]">{item.title}</h3>
                    <p className="text-sm text-[#475569]">{item.description}</p>
                  </div>
                  <p className="text-xs text-[#475569]">{item.meta || "—"}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <Button onClick={loadSettings} variant="outline" data-testid="refresh-website-status-button">
          <RefreshCcw className="mr-2 h-4 w-4" />
          Refresh website checks
        </Button>
      </TabsContent>

      <TabsContent value="domain" className="space-y-6">
        <Card className="neumorphic-card rounded-3xl">
          <CardHeader>
            <CardTitle>DNS overview</CardTitle>
            <CardDescription>These values show the live records currently resolving for friendshipad.com.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3 rounded-2xl bg-[#F8FAFC] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Nameservers</p>
              <p className="text-sm text-[#0F172A]">Expected: {domainData?.expected_nameserver}</p>
              <div className="space-y-1 text-sm text-[#475569]">
                {(domainData?.nameservers || []).map((item) => <p key={item}>{item}</p>)}
              </div>
            </div>
            <div className="space-y-3 rounded-2xl bg-[#F8FAFC] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Website records</p>
              <p className="text-sm text-[#475569]">@ → A record → {(domainData?.a_records || []).join(", ") || "Missing"}</p>
              <p className="text-sm text-[#475569]">www → CNAME → {(domainData?.www_cname || []).join(", ") || "Missing"}</p>
            </div>
            <div className="space-y-3 rounded-2xl bg-[#F8FAFC] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Email authentication</p>
              <p className="text-sm text-[#475569]">MX: {(domainData?.mx_records || []).join(" | ") || "Provider values not configured"}</p>
              <p className="text-sm text-[#475569]">SPF: {(domainData?.spf_records || []).join(" | ") || "Not found"}</p>
              <p className="text-sm text-[#475569]">DKIM: {(domainData?.dkim_records || []).join(" | ") || "Add provider selector to check"}</p>
              <p className="text-sm text-[#475569]">DMARC: {(domainData?.dmarc_records || []).join(" | ") || "Not found"}</p>
            </div>
            <div className="space-y-3 rounded-2xl bg-[#F8FAFC] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">SSL</p>
              <p className="text-sm text-[#475569]">Status: {domainData?.ssl_status?.valid ? "Valid" : "Needs attention"}</p>
              <p className="text-sm text-[#475569]">Expires: {formatDateTime(domainData?.ssl_status?.expires_at)}</p>
              <p className="text-sm text-[#475569]">Days remaining: {domainData?.ssl_status?.days_remaining ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="gemini" className="space-y-6">
        <Card className="neumorphic-card rounded-3xl">
          <CardHeader>
            <CardTitle>Gemini AI</CardTitle>
            <CardDescription>Gemini uses the Emergent-managed credential. Personal Gemini API keys are disabled.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Connection status</p>
                <div className="mt-3 flex items-center gap-2">
                  <StatusBadge
                    ok={!!aiData?.gemini_status?.configured}
                    label={aiData?.gemini_status?.configured ? "Configured" : "Not configured"}
                    testId="gemini-configured-badge"
                  />
                  <StatusBadge
                    ok={!aiData?.gemini_status?.last_api_error}
                    label={aiData?.gemini_status?.last_api_error ? "Last test failed" : "No recent API errors"}
                    testId="gemini-last-error-badge"
                  />
                </div>
                <p className="mt-3 text-sm text-[#475569]" data-testid="gemini-provider-status">Provider: {aiData?.gemini_status?.provider || "Emergent-managed Gemini"}</p>
                <p className="text-sm text-[#475569]" data-testid="gemini-credential-source-status">Credential: {aiData?.gemini_status?.masked_api_key || "—"}</p>
                <p className="text-sm text-[#475569]">Last success: {formatDateTime(aiData?.gemini_status?.last_successful_api_connection)}</p>
                <p className="text-sm text-[#475569]">Last API error: {aiData?.gemini_status?.last_api_error || "—"}</p>
              </div>
              <div className="space-y-3 rounded-2xl bg-[#F8FAFC] p-4" data-testid="gemini-managed-provider-panel">
                <p className="text-sm font-semibold text-[#0F172A]" data-testid="gemini-managed-provider-title">Emergent-managed Gemini</p>
                <p className="text-sm text-[#475569]" data-testid="gemini-managed-provider-description">Payment receipt checks use the managed Gemini service. No personal Gemini key is stored or sent from this app.</p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={testGemini} disabled={saving} variant="outline" data-testid="test-gemini-connection-button">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Test connection
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="verification" className="space-y-6">
        <Card className="neumorphic-card rounded-3xl">
          <CardHeader>
            <CardTitle>AI verification controls</CardTitle>
            <CardDescription>Control when approvals happen automatically and when orders move into manual review.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">AI verification</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#0F172A]">Enabled</span>
                  <Switch
                    checked={aiForm.enabled}
                    onCheckedChange={(checked) => setAiForm((prev) => ({ ...prev, enabled: checked }))}
                    data-testid="ai-verification-enabled-switch"
                  />
                </div>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Failed attempts</p>
                <p className="mt-3 text-3xl font-black text-[#0F172A]">{aiData?.failed_verification_attempts || 0}</p>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Suspicious activity</p>
                <p className="mt-3 text-3xl font-black text-[#0F172A]">{suspiciousCount}</p>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">Gemini API status</p>
                <p className="mt-3 text-sm text-[#475569]">{aiData?.gemini_status?.last_api_error || "Ready"}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="approval-threshold">Confidence threshold</Label>
                <Input
                  id="approval-threshold"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={aiForm.confidence_threshold}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, confidence_threshold: Number(event.target.value) }))}
                  data-testid="confidence-threshold-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual-threshold">Manual-review threshold</Label>
                <Input
                  id="manual-threshold"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={aiForm.manual_review_threshold}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, manual_review_threshold: Number(event.target.value) }))}
                  data-testid="manual-review-threshold-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gemini-model-name">Gemini model</Label>
                <Input
                  id="gemini-model-name"
                  value={aiForm.model_name}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, model_name: event.target.value }))}
                  data-testid="gemini-model-name-input"
                />
              </div>
            </div>

            <Button onClick={saveAiSettings} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="save-ai-verification-settings-button">
              Save AI verification settings
            </Button>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#0F172A]">Verification logs</h3>
              <div className="space-y-3">
                {(aiData?.verification_logs || []).map((log) => (
                  <div key={log.log_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`verification-log-${log.log_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0F172A]">Order {log.order_id}</p>
                        <p className="text-sm text-[#475569]">{formatDateTime(log.created_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        <StatusBadge ok={log.decision === "approve"} label={log.decision} testId={`verification-log-status-${log.log_id}`} />
                        <Badge className="bg-slate-700">Risk {log.risk_level}</Badge>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-[#475569]">Confidence: {Math.round((log.confidence || 0) * 100)}% • Risk score: {log.risk_score}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="email" className="space-y-6">
        <Card className="neumorphic-card rounded-3xl">
          <CardHeader>
            <CardTitle>Email settings</CardTitle>
            <CardDescription>Configure support mailboxes and verify managed transactional delivery without exposing secrets.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[
                ["provider_name", "Provider name"],
                ["smtp_host", "SMTP host"],
                ["smtp_port", "SMTP port"],
                ["smtp_username", "SMTP username"],
                ["imap_host", "IMAP host"],
                ["imap_port", "IMAP port"],
                ["dkim_selector", "DKIM selector"],
                ["support_email", "Support email"],
              ].map(([field, label]) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={`email-${field}`}>{label}</Label>
                  <Input
                    id={`email-${field}`}
                    value={emailForm[field]}
                    onChange={(event) => setEmailForm((prev) => ({ ...prev, [field]: event.target.value }))}
                    data-testid={`email-${field}-input`}
                  />
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center justify-between rounded-2xl bg-[#F8FAFC] p-4">
                <div>
                  <p className="font-semibold text-[#0F172A]">Use TLS</p>
                  <p className="text-sm text-[#475569]">Recommended for SMTP on port 587</p>
                </div>
                <Switch checked={emailForm.smtp_use_tls} onCheckedChange={(checked) => setEmailForm((prev) => ({ ...prev, smtp_use_tls: checked }))} data-testid="smtp-use-tls-switch" />
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-[#F8FAFC] p-4">
                <div>
                  <p className="font-semibold text-[#0F172A]">Use SSL</p>
                  <p className="text-sm text-[#475569]">Typical for SMTP on port 465</p>
                </div>
                <Switch checked={emailForm.smtp_use_ssl} onCheckedChange={(checked) => setEmailForm((prev) => ({ ...prev, smtp_use_ssl: checked }))} data-testid="smtp-use-ssl-switch" />
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-[#F8FAFC] p-4">
                <div>
                  <p className="font-semibold text-[#0F172A]">IMAP SSL</p>
                  <p className="text-sm text-[#475569]">Secure incoming mail</p>
                </div>
                <Switch checked={emailForm.imap_use_ssl} onCheckedChange={(checked) => setEmailForm((prev) => ({ ...prev, imap_use_ssl: checked }))} data-testid="imap-use-ssl-switch" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-emails">Notification emails</Label>
              <Input
                id="notification-emails"
                value={emailForm.notification_emails}
                onChange={(event) => setEmailForm((prev) => ({ ...prev, notification_emails: event.target.value }))}
                placeholder="a@friendshipad.com,b@friendshipad.com"
                data-testid="notification-emails-input"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={saveEmailSettings} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="save-email-settings-button">
                <Mail className="mr-2 h-4 w-4" />
                Save email settings
              </Button>
              <Button onClick={testEmail} disabled={saving} variant="outline" data-testid="test-email-connection-button">
                Test managed delivery
              </Button>
            </div>

            <div className="rounded-2xl bg-[#F8FAFC] p-5 text-sm text-[#475569]">
              <p>Managed delivery status: {emailData?.last_test_status || "Not tested"}</p>
              <p>Last success: {formatDateTime(emailData?.last_successful_test)}</p>
              <p>Last error: {emailData?.last_test_error || "—"}</p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#0F172A]">Official email accounts</h3>
              {(emailData?.mailboxes || []).map((mailbox) => (
                <div key={mailbox.email} className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-[1.2fr_1fr_auto]" data-testid={`mailbox-${mailbox.email}`}>
                  <div>
                    <p className="font-semibold text-[#0F172A]">{mailbox.email}</p>
                    <p className="text-sm text-[#475569]">Password status: {mailbox.masked_password || "Not saved"}</p>
                  </div>
                  <Input
                    type="password"
                    value={mailboxPasswords[mailbox.email] || ""}
                    onChange={(event) => setMailboxPasswords((prev) => ({ ...prev, [mailbox.email]: event.target.value }))}
                    placeholder="Update mailbox password"
                    data-testid={`mailbox-password-input-${mailbox.email}`}
                  />
                  <Button onClick={() => updateMailboxPassword(mailbox.email)} disabled={saving} variant="outline" data-testid={`save-mailbox-password-button-${mailbox.email}`}>
                    Secure update
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="security" className="space-y-6">
        <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
          <Card className="neumorphic-card rounded-3xl">
            <CardHeader>
              <CardTitle>Admin MFA</CardTitle>
              <CardDescription>Protect the admin dashboard with TOTP verification.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-wrap gap-2">
                <StatusBadge ok={!!mfaStatus?.enabled} label={mfaStatus?.enabled ? "Enabled" : "Disabled"} testId="mfa-enabled-badge" />
                <StatusBadge ok={!!mfaStatus?.verified_current_session} label={mfaStatus?.verified_current_session ? "Verified this session" : "Verification needed"} testId="mfa-session-badge" />
              </div>

              {!mfaStatus?.enabled && !setupPayload && (
                <Button onClick={startMfaSetup} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="start-mfa-setup-button">
                  Start MFA setup
                </Button>
              )}

              {setupPayload && (
                <div className="space-y-4 rounded-2xl bg-[#F8FAFC] p-5">
                  <img src={setupPayload.qr_code} alt="MFA QR code" className="mx-auto w-52 rounded-2xl bg-white p-3" />
                  <p className="text-sm text-[#475569]">Manual key: <span className="font-semibold text-[#0F172A]">{setupPayload.manual_entry_key}</span></p>
                  <Input value={setupCode} onChange={(event) => setSetupCode(event.target.value)} placeholder="Enter code from authenticator" data-testid="mfa-setup-code-input" />
                  <Button onClick={confirmMfaSetup} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="confirm-mfa-setup-button">
                    Confirm MFA setup
                  </Button>
                </div>
              )}

              {mfaStatus?.enabled && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3 rounded-2xl bg-[#F8FAFC] p-4">
                    <p className="font-semibold text-[#0F172A]">Verify current session</p>
                    <Input value={verifyCode} onChange={(event) => setVerifyCode(event.target.value)} placeholder="123456" data-testid="mfa-verify-session-input" />
                    <Button onClick={verifyMfaSession} disabled={saving} variant="outline" data-testid="mfa-verify-session-button">Verify session</Button>
                  </div>
                  <div className="space-y-3 rounded-2xl bg-[#FFF7ED] p-4">
                    <p className="font-semibold text-[#9A3412]">Disable MFA</p>
                    <Input value={disableCode} onChange={(event) => setDisableCode(event.target.value)} placeholder="Enter current code" data-testid="mfa-disable-code-input" />
                    <Button onClick={disableMfa} disabled={saving} variant="outline" data-testid="disable-mfa-button">Disable MFA</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="neumorphic-card rounded-3xl">
            <CardHeader>
              <CardTitle>Security posture</CardTitle>
              <CardDescription>Live security controls and operational activity.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-[#475569]">
              <p>CSRF protection: {securityData?.csrf_protection ? "Enabled" : "Disabled"}</p>
              <p>Secure cookies: {securityData?.secure_cookies ? "Enabled" : "Disabled"}</p>
              <p>Session expiration: {securityData?.session_expiration_days} days</p>
              <p>Rate limiting: {securityData?.rate_limiting ? "Enabled" : "Disabled"}</p>
              <p>Brute-force protection: {securityData?.brute_force_protection ? "Enabled" : "Disabled"}</p>
              <p>Failed auth attempts (24h): {securityData?.failed_auth_attempts_last_24h ?? 0}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="neumorphic-card rounded-3xl">
          <CardHeader>
            <CardTitle>Active sessions</CardTitle>
            <CardDescription>Recent signed-in sessions and MFA verification timestamps.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(securityData?.active_sessions || []).map((session, index) => (
              <div key={`${session.user_id}-${index}`} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`active-session-${index}`}>
                <p className="font-semibold text-[#0F172A]">{session.user_id}</p>
                <p className="text-sm text-[#475569]">Created: {formatDateTime(session.created_at)}</p>
                <p className="text-sm text-[#475569]">Expires: {formatDateTime(session.expires_at)}</p>
                <p className="text-sm text-[#475569]">MFA verified: {formatDateTime(session.mfa_verified_at)}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="neumorphic-card rounded-3xl">
          <CardHeader>
            <CardTitle>Audit logs</CardTitle>
            <CardDescription>Security, login, and admin settings events.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(securityData?.audit_logs || []).map((log) => (
              <div key={log.event_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`audit-log-${log.event_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{log.event_type}</p>
                    <p className="text-sm text-[#475569]">{formatDateTime(log.created_at)} • {log.actor}</p>
                  </div>
                  <Badge className={log.status === "success" ? "bg-green-600" : log.status === "failed" ? "bg-red-600" : "bg-amber-500"}>{log.status}</Badge>
                </div>
                <p className="mt-2 text-xs text-[#64748B]">IP: {log.ip_address}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};